import { type HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/shared/lib/utils";

/**
 * Status chip (design-system.md §4). A bordered mono pill. `positive` is the
 * lime brand state (LAUNCHED / RUNNING / APPROVED); `neutral` is a bare frame
 * (CHANNEL: EMAIL). Table status CELLS use bare colored text instead — see
 * <StatusText>.
 */
const chipVariants = cva(
  "inline-flex items-center rounded-ge-chip px-[9px] py-[3px] font-mono text-[10px] uppercase tracking-ge-table",
  {
    variants: {
      variant: {
        positive: "border border-ge-border-accent bg-ge-accent-tint text-ge-accent",
        neutral: "border border-ge-border-card text-ge-secondary",
        warning: "border border-ge-border-warn text-ge-warning",
        danger: "border border-ge-border-danger text-ge-danger",
      },
    },
    defaultVariants: { variant: "neutral" },
  },
);

export type StatusChipProps = HTMLAttributes<HTMLSpanElement> & VariantProps<typeof chipVariants>;

export function StatusChip({ className, variant, ...props }: StatusChipProps) {
  return <span className={cn(chipVariants({ variant }), className)} {...props} />;
}

/** Bare colored mono text for status CELLS inside tables (no chip frame). */
const statusTextVariants = cva("font-mono text-[11px] uppercase tracking-ge-table", {
  variants: {
    tone: {
      accent: "text-ge-accent",
      value: "text-ge-value",
      warning: "text-ge-warning",
      danger: "text-ge-danger",
      faint: "text-ge-faint",
    },
  },
  defaultVariants: { tone: "value" },
});

export type StatusTextProps = HTMLAttributes<HTMLSpanElement> &
  VariantProps<typeof statusTextVariants>;

export function StatusText({ className, tone, ...props }: StatusTextProps) {
  return <span className={cn(statusTextVariants({ tone }), className)} {...props} />;
}
